// Test-only completion outbox. These IDs cannot redeem production/SSV intents.
// Persist an SDK-earned test event before contacting our server so a network
// interruption does not make the player watch the same advertisement again.
const KEY = 'varnax-earned-test-rewards-v1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type Pending = { id: string; expiresAt: number };
let memory: Pending[] = [];
let memoryOverridesStorage = false;
const flights = new Map<string, Promise<void>>();
function read(): Pending[] {
  let stored: unknown = [];
  try { if (!memoryOverridesStorage) stored = JSON.parse(localStorage.getItem(KEY) ?? '[]'); } catch { /* Memory fallback. */ }
  const all = [...memory, ...(Array.isArray(stored) ? stored : [])] as Pending[];
  return [...new Map(all.filter(item => item && UUID.test(item.id) && Number.isFinite(item.expiresAt)
    && item.expiresAt > Date.now()).slice(-20).map(item => [item.id, item])).values()];
}
function save(items: Pending[]) {
  memory = items;
  try { if (items.length) localStorage.setItem(KEY, JSON.stringify(items)); else localStorage.removeItem(KEY); memoryOverridesStorage = false; }
  catch { memoryOverridesStorage = true; /* Do not resurrect removed entries from unwritable storage. */ }
}
export function rememberEarnedTestReward(id: string, expiresAt: number): void {
  if (!UUID.test(id) || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) return;
  save([...read().filter(item => item.id !== id), { id, expiresAt }].slice(-20));
}
export async function reconcileEarnedTestRewards(
  submit: (id: string) => Promise<{ completed: boolean }>, onlyId?: string,
): Promise<void> {
  for (const item of read().filter(item => !onlyId || item.id === onlyId)) {
    let flight = flights.get(item.id);
    if (!flight) {
      flight = (async () => {
        try {
          const result = await submit(item.id);
          if (result.completed === true) save(read().filter(pending => pending.id !== item.id));
        } catch (error) {
          // Terminal rejection (expired/other account/test mode disabled) is
          // never converted into a wallet grant or retried as a real SSV event.
          const status = error && typeof error === 'object' && 'status' in error ? error.status : undefined;
          if ([403, 404, 409, 410].includes(Number(status))) save(read().filter(pending => pending.id !== item.id));
        }
      })().finally(() => { flights.delete(item.id); });
      flights.set(item.id, flight);
    }
    await flight;
  }
}
